import { useEffect, useRef } from "react";
import { Grid, MultiSelect } from "@mantine/core";
import { SubDenialReason } from "@/__generated__/graphql";
import {
  shouldShowSubDenialReasonField,
  getSubDenialReasonOptions,
} from "shared/src/utils/sub-denial-reason-utils";

import styles from "./initial-referral-page.module.scss";

interface SubDenialReasonFieldProps {
  condition: string;
  denialReason: string;
  subDenialReasons: SubDenialReason[];
  disabled: boolean;
  onChange: (values: SubDenialReason[]) => void;
  onClear: () => void;
  error?: React.ReactNode;
}

export function SubDenialReasonField({
  condition,
  denialReason,
  subDenialReasons,
  disabled,
  onChange,
  onClear,
  error,
}: SubDenialReasonFieldProps) {
  const shouldShow = shouldShowSubDenialReasonField(condition, denialReason);
  const prevShouldShowRef = useRef(shouldShow);
  const onClearRef = useRef(onClear);
  onClearRef.current = onClear;

  useEffect(() => {
    if (prevShouldShowRef.current && !shouldShow) {
      onClearRef.current();
    }
    prevShouldShowRef.current = shouldShow;
  }, [shouldShow]);

  if (!shouldShow) {
    return null;
  }

  const options = getSubDenialReasonOptions(condition);

  return (
    <Grid mt="md">
      <Grid.Col>
        <MultiSelect
          label="Sub denial reasons"
          placeholder={
            subDenialReasons.length > 0 ? "" : "Select all that apply"
          }
          data={options}
          disabled={disabled}
          searchable
          className={styles.multiSelectWithoutChevron}
          value={subDenialReasons}
          onChange={(values) => onChange(values as SubDenialReason[])}
          error={error}
          data-testid="sub-denial-reason-input"
        />
      </Grid.Col>
    </Grid>
  );
}
